/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import isEmpty from 'lodash.isempty';
import {
    BlacklistValidatorFactory,
    DuplicateValueValidatorFactory,
    ExtensionValidatorFactory,
    RequiredValidatorFactory
} from 'src/core/types/form';

/**
 * A factory to create a required validator. A required validator is simply
 * one that succeeds only if the value given is non empty or whitespace.
 */
export const createRequiredValidator: RequiredValidatorFactory = () => (value: File) => {
    const trimmedValue: string = value.name.trim();

    return !isEmpty(trimmedValue);
};

/**
 * A factory to create an extension validator. An extension validator will succeed
 * when the given value has the required extension.
 *
 * @param extension A string of the extension required. Ex: 'txt', 'json', etc. The
 * extension should not be prefixed with a '.', only the string part of the extension
 * should be passed.
 */
export const createExtensionValidator: ExtensionValidatorFactory = (extension: string) => (value: File) => {
    const trimmedValue: string = value.name.trim().toLocaleLowerCase();

    return trimmedValue && trimmedValue.split('.').pop() === extension;
};

/**
 * Creates a validator that validates whether the given value is a duplicate
 * of the original value or not.
 *
 * @param original The original value that is being tested against.
 * @returns A form validator that validates against the original value.
 */
export const createDuplicateValueValidator: DuplicateValueValidatorFactory = (original: File) => (value: File) =>
    original.name.trim() !== value.name.trim();

/**
 * A factory for creating blacklist validators. A black list validator is one
 * that succeeds validation only if the value given does not contain any of the
 * characters defined in the blacklist.
 *
 * @param blacklist A string of the characters to blacklist. The characters should
 * be contiguous with no white spaces between them. The value to validate should not
 * contain any characters in this string.
 */
export const createBlacklistValidator: BlacklistValidatorFactory = ({ mode = 'exclude', characterList = '\\/:?&=*+()%@$~!#' }) => (
    value: File
) => {
    const trimmedValue: string = value.name.trim();
    // eslint-disable-next-line security/detect-non-literal-regexp
    const isValid = !new RegExp(`[${characterList}]+`, 'g').test(trimmedValue);

    return mode === 'exclude' ? isValid : !isValid;
};
